/**
 * Layout of the local Phigros skin (W2-01b; ARCHITECTURE.md §12, skin-and-assets.md S§2.1, DESIGN.md D23/R11),
 * shared by the dev/local Vite plugin `phiedit-local-skin` (tools/vite-plugin-local-skin.ts, runs in Node) and the
 * browser loader (localSkin.ts):
 *
 *  - the plugin serves the unzipped skin under `<base>__local-skin/`: `index.json` lists the files that exist, and
 *    every listed file is served by its exact name. Only the respack layout's names are ever served (info.yml, the
 *    nine images, click/drag/flick/ending sounds as .ogg/.wav/.mp3), so a request can never reach another file;
 *  - `hold.*` sounds are not served: Phira never loads them (S§1.4).
 *
 * This module is also loaded by Node (through the plugin, from vite.config.ts), so it imports nothing and uses only
 * erasable TypeScript. Its name lists equal formats/respack's RESPACK_IMAGES / RESPACK_SOUNDS (pinned by a test).
 */

/** The nine respack images (= RESPACK_IMAGES). */
export const LOCAL_SKIN_IMAGES = [
  'click',
  'hold',
  'flick',
  'drag',
  'click_mh',
  'hold_mh',
  'flick_mh',
  'drag_mh',
  'hit_fx',
] as const;
/** The respack sounds Phira loads (= RESPACK_SOUNDS; `hold` is never used). */
export const LOCAL_SKIN_SOUNDS = ['click', 'drag', 'flick', 'ending'] as const;

/** Route of the local skin, relative to the app's base URL. */
export const LOCAL_SKIN_ROUTE = '__local-skin/';
/** File listing served next to the skin files. */
export const LOCAL_SKIN_INDEX = 'index.json';
/** Sound extensions in Phira's lookup order (S§2.1). */
export const LOCAL_SKIN_SOUND_EXTS = ['ogg', 'wav', 'mp3'] as const;

/** Files a usable skin must contain: info.yml and the nine images. */
export const LOCAL_SKIN_REQUIRED: readonly string[] = ['info.yml', ...LOCAL_SKIN_IMAGES.map((n) => `${n}.png`)];
/** Optional sound files (the first existing extension of each name is the one Phira uses). */
export const LOCAL_SKIN_OPTIONAL: readonly string[] = LOCAL_SKIN_SOUNDS.flatMap((s) =>
  LOCAL_SKIN_SOUND_EXTS.map((e) => `${s}.${e}`),
);
/** Every file name the local skin route may serve, in a stable order. */
export const LOCAL_SKIN_FILES: readonly string[] = [...LOCAL_SKIN_REQUIRED, ...LOCAL_SKIN_OPTIONAL];

const ALLOWED: ReadonlySet<string> = new Set(LOCAL_SKIN_FILES);

/** Body of `index.json`. */
export interface LocalSkinIndex {
  readonly files: readonly string[];
}

export function isLocalSkinFile(name: string): boolean {
  return ALLOWED.has(name);
}

/** Whether a set of file names holds a complete skin (info.yml and the nine images). */
export function isLocalSkinComplete(files: Iterable<string>): boolean {
  const have = new Set(files);
  return LOCAL_SKIN_REQUIRED.every((f) => have.has(f));
}

/** Content type the plugin sends for a served file. */
export function localSkinContentType(name: string): string {
  if (name === LOCAL_SKIN_INDEX) return 'application/json; charset=utf-8';
  if (name.endsWith('.yml')) return 'text/yaml; charset=utf-8';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.ogg')) return 'audio/ogg';
  if (name.endsWith('.wav')) return 'audio/wav';
  if (name.endsWith('.mp3')) return 'audio/mpeg';
  return 'application/octet-stream';
}

export type LocalSkinRequest =
  | { readonly kind: 'index' }
  | { readonly kind: 'file'; readonly name: string }
  /** Under the route but not a file the route serves (404). */
  | { readonly kind: 'notFound' };

/**
 * Classifies a request URL (path + optional query) for the local skin route under `base` ('/' or '/sub/').
 * Returns null for URLs outside the route (the next middleware handles them).
 */
export function matchLocalSkinRequest(url: string, base: string): LocalSkinRequest | null {
  const normBase = base.endsWith('/') ? base : `${base}/`;
  const prefix = `${normBase.startsWith('/') ? normBase : `/${normBase}`}${LOCAL_SKIN_ROUTE}`;
  const pathOnly = url.split(/[?#]/, 1)[0] ?? '';
  if (!pathOnly.startsWith(prefix)) return null;
  let rest: string;
  try {
    rest = decodeURIComponent(pathOnly.slice(prefix.length));
  } catch {
    return { kind: 'notFound' };
  }
  if (rest === LOCAL_SKIN_INDEX) return { kind: 'index' };
  return isLocalSkinFile(rest) ? { kind: 'file', name: rest } : { kind: 'notFound' };
}

/** Reads an `index.json` body; null when it is not a listing of local skin files. */
export function parseLocalSkinIndex(v: unknown): LocalSkinIndex | null {
  if (typeof v !== 'object' || v === null) return null;
  const files = (v as { readonly files?: unknown }).files;
  if (!Array.isArray(files)) return null;
  const out: string[] = [];
  for (const f of files as readonly unknown[]) {
    if (typeof f !== 'string' || !isLocalSkinFile(f)) return null;
    if (!out.includes(f)) out.push(f);
  }
  return { files: out };
}
